import React from "react";

type History<T> = {
  past: T[];
  present: T;
  future: T[];
};

export type UndoableValue<T> = {
  value: T;
  set: (next: T | ((current: T) => T)) => void;
  /** Übernimmt einen Wert ohne Verlaufseintrag — für Laden und Speichern. */
  reset: (next: T) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};

/** Mehr Schritte braucht niemand, und der Speicherbedarf bleibt überschaubar. */
const MAX_STEPS = 50;

/**
 * Ein Wert mit Rückgängig und Wiederherstellen.
 *
 * Der Verlauf umfasst den gesamten Entwurf: Das Verschieben, Konfigurieren, Ein- und Ausblenden
 * sowie die Einstellungen eines Formats lassen sich damit gleich verlässlich zurücknehmen.
 */
export function useUndoableValue<T>(initial: T): UndoableValue<T> {
  const [history, setHistory] = React.useState<History<T>>({ past: [], present: initial, future: [] });

  const set = React.useCallback((next: T | ((current: T) => T)) => {
    setHistory((current) => {
      const value = typeof next === "function" ? (next as (value: T) => T)(current.present) : next;
      if (Object.is(value, current.present)) return current;
      return { past: [...current.past, current.present].slice(-MAX_STEPS), present: value, future: [] };
    });
  }, []);

  const reset = React.useCallback((next: T) => {
    setHistory({ past: [], present: next, future: [] });
  }, []);

  const undo = React.useCallback(() => {
    setHistory((current) => {
      if (current.past.length === 0) return current;
      const previous = current.past[current.past.length - 1];
      return { past: current.past.slice(0, -1), present: previous, future: [current.present, ...current.future].slice(0, MAX_STEPS) };
    });
  }, []);

  const redo = React.useCallback(() => {
    setHistory((current) => {
      if (current.future.length === 0) return current;
      const [next, ...rest] = current.future;
      return { past: [...current.past, current.present].slice(-MAX_STEPS), present: next, future: rest };
    });
  }, []);

  return { value: history.present, set, reset, undo, redo, canUndo: history.past.length > 0, canRedo: history.future.length > 0 };
}
